/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   UnifiedRunsExplorer.tsx
 *     Feature         :   191-pipeline-monitoring-platform
 *     Description     :   One paginated runs table across ALL registered processes —
 *                         registry-fed source multi-select (chips), status + date filters,
 *                         sortable headers (server-side sort), dual top/bottom horizontal
 *                         scrollbars, page-size selector, row click → detail drawer.
 *     Constitution    :   v1.18.0 | Principle XXXVII (Code Header Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-06-11
 * =========================================================================================
 */

import React, { useState } from 'react';
import type {
  MonitoredPipeline,
  PipelineRunSortBy,
  PipelineRunStatus,
} from '@shared/types/monitoredPipelines';
import { useUnifiedRuns } from '../../hooks/useMonitoredPipelines';
import { DualScrollTable, PaginationControls } from '../common/TableControls';
import { LoadingSpinner } from './LoadingSpinner';
import { ErrorBanner } from './ErrorBanner';
import {
  PipelineStatusBadge,
  formatDuration,
  formatDateTime,
  formatNumber,
} from './PipelineStatusBadge';

export interface UnifiedRunsExplorerProps {
  /** Flat registry (presence-filtered options are derived here). */
  pipelines: MonitoredPipeline[];
  onRunSelect: (pipelineKey: string, batchId: string) => void;
  selectedBatchId?: string | null;
}

const STATUS_OPTIONS: Array<{ value: PipelineRunStatus | 'All'; label: string }> = [
  { value: 'All',       label: 'All Statuses' },
  { value: 'Running',   label: 'Running' },
  { value: 'Succeeded', label: 'Succeeded' },
  { value: 'Failed',    label: 'Failed' },
  { value: 'Partial',   label: 'Partial' },
  { value: 'NoOp',      label: 'No-op' },
  { value: 'Skipped',   label: 'Skipped' },
  { value: 'Abandoned', label: 'Abandoned' },
];

type SortDir = 'asc' | 'desc';

function SortHeader({
  label,
  col,
  sortBy,
  sortDir,
  onSort,
  align = 'left',
}: {
  label: string;
  col: PipelineRunSortBy;
  sortBy: PipelineRunSortBy;
  sortDir: SortDir;
  onSort: (col: PipelineRunSortBy, dir: SortDir) => void;
  align?: 'left' | 'right';
}) {
  const isActive = sortBy === col;
  return (
    <button
      type="button"
      className={`flex items-center gap-0.5 ${align === 'right' ? 'ml-auto' : ''} hover:text-[var(--color-text-primary)] transition-colors group uppercase tracking-wide`}
      onClick={() => onSort(col, isActive ? (sortDir === 'asc' ? 'desc' : 'asc') : 'desc')}
      aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      data-testid={`sort-${col}`}
    >
      {label}
      <span className={`ml-0.5 ${isActive ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-secondary)] group-hover:text-[var(--color-text-secondary)]'}`}>
        {isActive && sortDir === 'asc' ? '↑' : '↓'}
      </span>
    </button>
  );
}

export function UnifiedRunsExplorer({
  pipelines,
  onRunSelect,
  selectedBatchId = null,
}: UnifiedRunsExplorerProps) {
  const present = pipelines.filter((p) => p.presentInEnv);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]); // empty = all
  const [statusFilter, setStatusFilter] = useState<PipelineRunStatus | 'All'>('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState<PipelineRunSortBy>('startTime');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { data, isLoading, error, isFetching } = useUnifiedRuns({
    keys: selectedKeys.length ? selectedKeys : undefined,
    status: statusFilter === 'All' ? undefined : statusFilter,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    page,
    pageSize,
    sortBy,
    sortDir,
  });

  const toggleKey = (key: string) => {
    setPage(1);
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleSort = (col: PipelineRunSortBy, dir: SortDir) => {
    setSortBy(col);
    setSortDir(dir);
    setPage(1);
  };

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const keyLabel = new Map(pipelines.map((p) => [p.pipelineKey, p.displayName]));
  const th = 'px-2 py-1.5 text-xs font-semibold text-[var(--color-text-secondary)]';

  return (
    <div className="space-y-3" data-testid="unified-runs-explorer">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-1.5" data-testid="source-multiselect">
          <span className="text-xs font-medium text-[var(--color-text-secondary)] mr-1">Sources:</span>
          <button
            type="button"
            onClick={() => { setSelectedKeys([]); setPage(1); }}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              selectedKeys.length === 0
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-blue-400'
            }`}
          >
            All
          </button>
          {present.map((p) => (
            <button
              key={p.pipelineKey}
              type="button"
              onClick={() => toggleKey(p.pipelineKey)}
              data-testid={`source-chip-${p.pipelineKey}`}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                selectedKeys.includes(p.pipelineKey)
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-blue-400'
              }`}
            >
              {p.displayName}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as PipelineRunStatus | 'All'); setPage(1); }}
            data-testid="status-filter"
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1 text-xs text-[var(--color-text-secondary)]"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            data-testid="from-date-filter"
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1 text-xs text-[var(--color-text-secondary)]"
            aria-label="From date"
          />
          <span className="text-xs text-[var(--color-text-secondary)]">–</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            data-testid="to-date-filter"
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1 text-xs text-[var(--color-text-secondary)]"
            aria-label="To date"
          />
        </div>
      </div>

      {error ? (
        <ErrorBanner message="Failed to load runs" />
      ) : isLoading || !data ? (
        // Spec 298 FR-006 (rows 14-15): no runs yet means pending or paused (isLoading is false while paused),
        // never an empty table reading "0 runs across 0 sources".
        <LoadingSpinner />
      ) : (
        <div className="rounded-lg border border-[var(--color-border)]">
          <DualScrollTable>
            <table className="min-w-full divide-y divide-[var(--color-border)] text-sm">
              <thead className="border-b-2 border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
                <tr className="text-left">
                  <th className={th}>PIPELINE</th>
                  <th className={th}>
                    <SortHeader label="RUN" col="batchId" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  </th>
                  <th className={th}>
                    <SortHeader label="SOURCE" col="source" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  </th>
                  <th className={th}>
                    <SortHeader label="INSTANCE" col="instance" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  </th>
                  <th className={th}>
                    <SortHeader label="MODE" col="mode" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  </th>
                  <th className={th}>
                    <SortHeader label="START" col="startTime" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  </th>
                  <th className={th}>
                    <SortHeader label="DURATION" col="duration" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  </th>
                  <th className={`${th} text-right`}>ITEMS ✓/✗</th>
                  <th className={`${th} text-right`}>
                    <SortHeader label="ROWS" col="rows" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                  </th>
                  <th className={th}>
                    <SortHeader label="STATUS" col="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)] bg-[var(--color-bg-card)]">
                {(data?.runs ?? []).map((run) => (
                  <tr
                    key={`${run.pipelineKey}:${run.batchId}`}
                    onClick={() => onRunSelect(run.pipelineKey, run.batchId)}
                    data-testid={`run-row-${run.batchId}`}
                    className={`cursor-pointer transition-colors ${
                      selectedBatchId === run.batchId
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : 'hover:bg-[var(--color-bg-subtle)]'
                    }`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap font-medium text-[var(--color-text-primary)]">
                      {keyLabel.get(run.pipelineKey) ?? run.pipelineKey}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-[var(--color-text-secondary)] max-w-[16rem] truncate" title={run.batchId}>
                      {run.batchId}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-[var(--color-text-secondary)]">{run.sourceName ?? '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-[var(--color-text-secondary)]">{run.instance}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-[var(--color-text-secondary)]">{run.mode}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-[var(--color-text-secondary)]">{formatDateTime(run.startTime)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-[var(--color-text-secondary)]">{formatDuration(run.durationSeconds)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-[var(--color-text-secondary)]">
                      {formatNumber(run.itemsSucceeded)} / {formatNumber(run.itemsFailed)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-[var(--color-text-secondary)]">{formatNumber(run.totalRowsProcessed)}</td>
                    <td className="px-3 py-2 whitespace-nowrap"><PipelineStatusBadge status={run.status} /></td>
                  </tr>
                ))}
                {(data?.runs ?? []).length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-3 py-8 text-center text-sm text-[var(--color-text-secondary)]">
                      No runs match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </DualScrollTable>

          <PaginationControls
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            start={(page - 1) * pageSize}
            shown={data.runs.length}
            onPage={(p) => setPage(Math.min(totalPages, Math.max(1, p)))}
            onPageSize={(s) => { setPageSize(s); setPage(1); }}
          />

          <div className="px-4 pb-2 text-xs text-[var(--color-text-secondary)]">
            {total} runs across {data.keys.length} source{data.keys.length === 1 ? '' : 's'}
            {isFetching ? ' · refreshing…' : ''}
          </div>
        </div>
      )}
    </div>
  );
}
